import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Formik, Form, Field } from 'formik';
import { isEmail } from 'validator';
import { useDispatch } from 'react-redux';
import * as Dialog from '@radix-ui/react-dialog';
import { css } from '@emotion/react';
import CloseSVG from '@birdi/icons/close.svg';
import { closeButtonCSS, contentCSS, portalCSS } from '@birdi/modal-kit';
import { BirdiTheme, buttonBlue } from '@birdi/theme';
import { blocks, forms, breakpoints } from '@birdi/theme';
import { stroot, organisationUsers } from '@birdi/js-sdk';
import RoleSelect from '@birdi/select/RoleSelect';
import WorkspaceSelect from '@birdi/select/WorkspaceSelect';
import { LocationSelect } from '@birdi/google-places-autocomplete';
import {
  loadSeats,
  totalSeats,
  useSeatsSelector,
} from '../plan-and-usage/seats/seats-reducer';
import * as userActions from './reducer';
import { useMetaSelector } from '../../meta/reducer';

interface InviteUserModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  workspaceId?: string | null;
}

export const InviteUserModal = ({
  open,
  setOpen,
  workspaceId = null,
}: InviteUserModalProps) => {
  // TODO: Reimplement dummy vals
  // TODO: Seats reducer
  const location = useLocation();
  const dispatch = useDispatch();
  const seats = useSeatsSelector();

  const meta = useMetaSelector();
  const primaryOrg = !meta.workspace;

  const isOrganisationAdmin = meta.permissions.includes(
    'organisation:user:create',
  );
  const canCreateWorkspace = meta.permissions.includes(
    'organisation:workspace:create',
  );

  const workspaceEnable =
    meta.features.includes('add-workspaces') && primaryOrg;

  const adminUsersPage = location.pathname.includes('admin-users');

  const workspaceSelection =
    isOrganisationAdmin &&
    canCreateWorkspace &&
    workspaceEnable &&
    adminUsersPage;

  const [userExists, setUserExists] = useState(false);

  useEffect(() => {
    dispatch(userActions.getUsers());
    loadSeats(dispatch);
  }, []);

  // end dummy vals
  const addUser = async (userObj, { setSubmitting, setFieldError }) => {
    setSubmitting(false);

    const preparedUser = {
      ...userObj,
      roleId: userObj.role.value,
      ...(userObj.workspace && { workspaceId: userObj.workspace.value }),
    };
    delete preparedUser.role;
    delete preparedUser.workspace;
    if (workspaceId) preparedUser.workspaceId = workspaceId;

    try {
      // TODO: Add user directly into state
      const res = await organisationUsers.create(
        stroot('no8ief'),
        preparedUser,
      );
      if (res.status === 201) {
        window.location.reload();
      }
    } catch (err) {
      if (err.status === 409) {
        setFieldError('email', (err as Error).message);
      } else {
        const error = (err as Error).message;
        if (typeof error.message === 'string') {
          setFieldError('general', error.message);
        } else {
          setFieldError('general', 'something went wrong');
        }
      }
    }
  };

  const validate = (values: Record<string, unknown>) => {
    const errors: Record<string, string> = {};
    const seatLimit = meta.organisation?.planData?.userLimitSeats;
    if (!values.fname) errors.fname = 'First name is required';
    if (!values.lname) errors.lname = 'Last name is required';
    if (!values.email) errors.email = 'Email is required';
    if (!values.role?.label) {
      // role id is required
      errors.role = 'required';
    } else if (
      values.role.label !== 'Guest' &&
      seatLimit &&
      totalSeats(seats) >= seatLimit
    ) {
      // User with free subscription shouldn't be able to invite non guest members
      errors.role = 'upgrade';
    }
    if (typeof values.email !== 'string' || !isEmail(values.email)) {
      errors.email = 'Please use a valid email address';
    }
    return errors;
  };

  const checkEmail = async (email) => {
    if (!email || !isEmail(email)) {
      setUserExists(false);
      return;
    }

    try {
      const res = await organisationUsers.checkExisting(stroot('boh2oh'), {
        email,
      });
      if (res.body.exists) {
        setUserExists(true);
      }
    } catch (err) {
      // console.error('Error checking email:', err);
    }
  };

  const onOpenChange = (status: boolean) => {
    setUserExists(false);
    setOpen(status);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay />

        <div css={portalCSS}>
          <Dialog.Content
            css={(theme: BirdiTheme) => css`
              ${contentCSS(theme)};
              overflow-y: auto;
            `}
          >
            <Dialog.Close css={closeButtonCSS}>
              <CloseSVG />
            </Dialog.Close>
            <Formik
              initialValues={{
                fname: '',
                lname: '',
                email: '',
                role: null,
              }}
              onSubmit={addUser}
              validate={validate}
            >
              {({ handleSubmit, errors, touched, setFieldValue, isValid }) => (
                <Form css={blocks.formStyles}>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      justify-content: center;
                      align-items: center;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        justify-content: center;
                      `}
                    >
                      <h1
                        css={css`
                          align-text: center;
                          margin: 1.5em 0 0.5em 0;
                          font-size: 1.5em;
                        `}
                      >
                        Invite users to your workspace
                      </h1>
                    </div>
                  </div>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                      width: 100%;
                      margin-top: 2em;
                      @media (max-width: ${breakpoints.xs}) {
                        flex-direction: column;
                      }
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        flex: 1;
                        flex-direction: column;
                        margin-right: 0.5em;
                      `}
                    >
                      <label htmlFor="fname">
                        First name
                        <Field
                          type="text"
                          name="fname"
                          id="fname"
                          placeholder="Enter first name"
                        />
                      </label>
                      {errors.fname && touched.fname && (
                        <div css={forms.errorMessage}>{errors.fname}</div>
                      )}
                      <label htmlFor="lname">
                        Last name
                        <Field
                          type="text"
                          name="lname"
                          id="lname"
                          placeholder="Enter last name"
                        />
                      </label>
                      {errors.lname && touched.lname && (
                        <div css={forms.errorMessage}>{errors.lname}</div>
                      )}
                      <label htmlFor="email">
                        Email
                        <Field
                          type="text"
                          name="email"
                          id="email"
                          placeholder="Enter email"
                          onChange={(e) => {
                            setFieldValue('email', e.target.value);
                            checkEmail(e.target.value);
                          }}
                        />
                      </label>
                      {errors.email && touched.email && (
                        <div css={forms.errorMessage}>{errors.email}</div>
                      )}
                      {userExists && (
                        <div css={forms.errorMessage}>
                          This email is already associated with your
                          organization. To edit this user’s details, visit their
                          profile via the Users management page.
                        </div>
                      )}
                      <label htmlFor="location">
                        Location
                        <LocationSelect
                          inputId="location"
                          onLocationSelect={(geocode) => {
                            setFieldValue('location', geocode.location);
                          }}
                          placeholder="Select location"
                        />
                      </label>
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex: 1;
                        flex-direction: column;
                        margin-left: 0.5em;
                      `}
                    >
                      <label htmlFor="phone">
                        Phone Number
                        <Field
                          type="text"
                          name="phone"
                          id="phone"
                          placeholder="Enter phone number"
                        />
                      </label>
                      <label htmlFor="position">
                        Job Title
                        <Field
                          type="text"
                          name="position"
                          id="position"
                          placeholder="Enter job title"
                        />
                      </label>
                      {workspaceSelection && (
                        <label
                          htmlFor="workspace"
                          css={css`
                            margin-bottom: 1em;
                          `}
                        >
                          Workspace
                          <WorkspaceSelect
                            placement="top"
                            onChange={(workspace) => {
                              setFieldValue('workspace', workspace);
                            }}
                            placeholder="Select workspace"
                            defaultValue={{
                              value: meta.organisation.id,
                              label: meta.organisation.name,
                            }}
                          />
                        </label>
                      )}
                      <label htmlFor="role">
                        Role
                        <RoleSelect
                          placement="top"
                          onChange={(role) => {
                            setFieldValue('role', role);
                          }}
                          placeholder="Select role"
                        />
                      </label>
                      {errors.role === 'required' && (
                        <div
                          css={css`
                            ${forms.errorMessage};
                            margin-top: 0.25em;
                          `}
                        >
                          Role is required
                        </div>
                      )}
                      {errors.role === 'upgrade' && (
                        <div>
                          {!primaryOrg ? (
                            <span
                              css={css`
                                ${forms.errorMessage};
                                margin-top: 0.25em;
                              `}
                            >
                              No more paid seats available. A team member from
                              the primary organisation needs to upgrade your
                              plan.
                            </span>
                          ) : (
                            <span
                              css={css`
                                ${forms.errorMessage};
                                margin-top: 0.25em;
                              `}
                            >
                              You have used all your paid seats, You can either
                              invite a new user as a guest or &nbsp;
                              <Link
                                to="/admin-users?modal=seats"
                                css={(theme: BirdiTheme) =>
                                  `color: ${theme.deepOcean}; text-decoration: underline; cursor: pointer`
                                }
                              >
                                upgrade your plan.
                              </Link>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {errors.general && (
                    <div
                      css={css`
                        ${forms.errorMessage};
                        margin-top: 1em;
                      `}
                    >
                      {errors.general}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!isValid || userExists}
                    css={(theme: BirdiTheme) => css`
                      ${buttonBlue(theme)}
                      margin-top: 2rem;
                      margin-left: auto;
                    `}
                  >
                    Send invite
                  </button>
                </Form>
              )}
            </Formik>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
